import React, { useEffect, useState } from 'react';
import AppShell from '../components/AppShell';
import api from '../lib/api';
import { LoadingBlock, EmptyState } from '../components/States';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import ImageUpload from '../components/ImageUpload';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ChevronRight, Plus, Trash2, Pencil, Layers, X } from 'lucide-react';
import { toast } from 'sonner';

import { errMsg } from '../lib/errors';
function SpecFieldEditor({ field, onChange, onRemove }) {
    const isSelect = field.type === 'select';
    const [optInput, setOptInput] = useState('');
    return (
        <div className="rounded-lg border p-3 bg-neutral-50 space-y-2" data-testid="spec-field-editor">
            <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-4">
                    <Label className="text-xs">Field Key</Label>
                    <Input value={field.key} onChange={e => onChange({ ...field, key: e.target.value.replace(/\s+/g, '_').toLowerCase() })}
                        placeholder="pot_size" data-testid="category-specs-field-key-input" />
                </div>
                <div className="col-span-4">
                    <Label className="text-xs">Label</Label>
                    <Input value={field.label} onChange={e => onChange({ ...field, label: e.target.value })} placeholder="Pot Size" data-testid="category-specs-field-name-input" />
                </div>
                <div className="col-span-3">
                    <Label className="text-xs">Type</Label>
                    <Select value={field.type} onValueChange={v => onChange({ ...field, type: v, options: v === 'select' ? (field.options || []) : null })}>
                        <SelectTrigger data-testid="category-specs-field-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="text">Text</SelectItem>
                            <SelectItem value="number">Number</SelectItem>
                            <SelectItem value="select">Select</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="col-span-1 flex justify-end">
                    <Button variant="ghost" size="icon" onClick={onRemove} data-testid="spec-field-remove-button"><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </div>
            </div>
            <div className="flex items-center gap-2">
                <Switch checked={field.required} onCheckedChange={v => onChange({ ...field, required: v })} data-testid="category-specs-field-required-switch" />
                <Label className="text-xs">Required</Label>
            </div>
            {isSelect && (
                <div>
                    <Label className="text-xs">Options</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                        {(field.options || []).map((o, idx) => (
                            <span key={idx} className="inline-flex items-center gap-1 bg-white border rounded-md px-2 py-0.5 text-xs">
                                {o}
                                <button type="button" onClick={() => onChange({ ...field, options: field.options.filter((_, i) => i !== idx) })}>
                                    <X className="h-3 w-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                    <div className="flex gap-2 mt-2">
                        <Input value={optInput} onChange={e => setOptInput(e.target.value)} placeholder="Add option and press +" data-testid="spec-field-option-input"
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (optInput.trim()) { onChange({ ...field, options: [...(field.options || []), optInput.trim()] }); setOptInput(''); } } }}
                        />
                        <Button type="button" size="sm" variant="outline" onClick={() => { if (optInput.trim()) { onChange({ ...field, options: [...(field.options || []), optInput.trim()] }); setOptInput(''); } }}>Add</Button>
                    </div>
                </div>
            )}
        </div>
    );
}

function CategoryDialog({ open, onOpenChange, editing, parents, onSaved }) {
    const isEdit = !!editing;
    const [form, setForm] = useState({
        name: '', parent_id: null, description: '', image: '', hsn_code: '', active: true, sort_order: 0, spec_fields: []
    });
    useEffect(() => {
        if (editing) setForm({
            name: editing.name || '', parent_id: editing.parent_id || null,
            description: editing.description || '', image: editing.image || '',
            hsn_code: editing.hsn_code || '',
            active: editing.active !== false, sort_order: editing.sort_order || 0,
            spec_fields: editing.spec_fields || []
        });
        else setForm({ name: '', parent_id: null, description: '', image: '', hsn_code: '', active: true, sort_order: 0, spec_fields: [] });
    }, [editing, open]);

    const save = async () => {
        try {
            const body = { ...form };
            if (body.parent_id === 'none') body.parent_id = null;
            if (isEdit) await api.patch(`/categories/${editing.id}`, body);
            else await api.post('/categories', body);
            toast.success(isEdit ? 'Category updated' : 'Category created');
            onSaved();
            onOpenChange(false);
        } catch (e) { toast.error(errMsg(e, 'Save failed')); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{isEdit ? 'Edit Category' : 'New Category'}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Name</Label>
                            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="category-name-input" />
                        </div>
                        <div>
                            <Label>Parent</Label>
                            <Select value={form.parent_id || 'none'} onValueChange={v => setForm({ ...form, parent_id: v === 'none' ? null : v })}>
                                <SelectTrigger data-testid="category-parent-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">(no parent - root)</SelectItem>
                                    {parents.filter(p => p.id !== editing?.id).map(p => (
                                        <SelectItem key={p.id} value={p.id}>{'—'.repeat(p.level)} {p.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <Label>Description</Label>
                        <Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label>Category Image</Label>
                            <ImageUpload
                                value={form.image}
                                folder="categories"
                                testId="category-image"
                                onChange={(url) => setForm({ ...form, image: url })}
                                size="md"
                            />
                        </div>
                        <div>
                            <Label>HSN Code <span className="text-xs text-neutral-500">(auto-inherits to products)</span></Label>
                            <Input value={form.hsn_code} onChange={e => setForm({ ...form, hsn_code: e.target.value })} placeholder="e.g. 0602 / 3924 / 8201" data-testid="category-hsn-input" />
                            <div className="flex items-center gap-2 mt-4">
                                <Switch checked={form.active} onCheckedChange={v => setForm({ ...form, active: v })} />
                                <Label>Active</Label>
                            </div>
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <Label>Specification fields</Label>
                            <Button type="button" size="sm" variant="outline" data-testid="category-specs-add-field-button"
                                onClick={() => setForm({ ...form, spec_fields: [...form.spec_fields, { key: '', label: '', type: 'text', required: false, options: [] }] })}
                            >
                                <Plus className="h-3.5 w-3.5 mr-1" /> Add field
                            </Button>
                        </div>
                        <div className="space-y-2">
                            {form.spec_fields.length === 0 && <div className="text-xs text-neutral-500">No spec fields defined. Add fields like Pot Size, Color, etc.</div>}
                            {form.spec_fields.map((f, idx) => (
                                <SpecFieldEditor key={idx} field={f}
                                    onChange={(nf) => setForm({ ...form, spec_fields: form.spec_fields.map((x, i) => i === idx ? nf : x) })}
                                    onRemove={() => setForm({ ...form, spec_fields: form.spec_fields.filter((_, i) => i !== idx) })}
                                />
                            ))}
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={save} data-testid="category-save-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>Save</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function TreeNodeRow({ node, depth, open, onToggle, onEdit, onDelete }) {
    const hasChildren = node.children && node.children.length > 0;
    const rotate = open ? 'rotate-90' : '';
    return (
        <div className="flex items-center gap-2 py-2 px-2 rounded hover:bg-neutral-50" style={{ paddingLeft: depth * 16 + 8 }}>
            {hasChildren ? (
                <button onClick={onToggle} className="p-0.5">
                    <ChevronRight className={'h-4 w-4 transition-transform ' + rotate} />
                </button>
            ) : <span className="w-5" />}
            <Layers className="h-4 w-4 text-neutral-400" />
            {node.image ? (
                <img src={node.image} alt="" className="h-8 w-8 rounded object-cover border" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            ) : null}
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{node.name}</div>
                <div className="text-xs text-neutral-500">
                    {(node.spec_fields || []).length} spec fields · {node.active ? 'Active' : 'Inactive'}
                    {node.hsn_code ? <> · HSN <span className="font-mono">{node.hsn_code}</span></> : null}
                </div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => onEdit(node)} data-testid={'category-edit-' + node.id}><Pencil className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" onClick={() => onDelete(node)} data-testid={'category-delete-' + node.id}><Trash2 className="h-4 w-4 text-red-600" /></Button>
        </div>
    );
}

function flattenTree(nodes, depth = 0, openMap, acc = []) {
    for (const node of nodes) {
        acc.push({ node, depth });
        const isOpen = openMap[node.id] !== undefined ? openMap[node.id] : depth < 1;
        if (isOpen && node.children && node.children.length > 0) {
            flattenTree(node.children, depth + 1, openMap, acc);
        }
    }
    return acc;
}

function TreeView({ tree, onEdit, onDelete }) {
    const [openMap, setOpenMap] = useState({});
    const toggle = (id, depth) => setOpenMap((m) => {
        const current = m[id] !== undefined ? m[id] : depth < 1;
        return { ...m, [id]: !current };
    });
    const rows = flattenTree(tree, 0, openMap);
    return (
        <div data-testid="categories-tree">
            {rows.map(({ node, depth }) => {
                const isOpen = openMap[node.id] !== undefined ? openMap[node.id] : depth < 1;
                return (
                    <TreeNodeRow
                        key={node.id}
                        node={node}
                        depth={depth}
                        open={isOpen}
                        onToggle={() => toggle(node.id, depth)}
                        onEdit={onEdit}
                        onDelete={onDelete}
                    />
                );
            })}
        </div>
    );
}

export default function Categories() {
    const [tree, setTree] = useState([]);
    const [flat, setFlat] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [tRes, fRes] = await Promise.all([api.get('/categories/tree'), api.get('/categories')]);
            setTree(tRes.data); setFlat(fRes.data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const doDelete = async (n) => {
        if (!window.confirm(`Delete category "${n.name}"?`)) return;
        try {
            await api.delete(`/categories/${n.id}`);
            toast.success('Deleted'); load();
        } catch (e) { toast.error(errMsg(e, 'Delete failed')); }
    };

    return (
        <AppShell pageTitle="Categories" pageActions={
            <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-category-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>
                <Plus className="h-4 w-4 mr-2" /> Add Category
            </Button>
        }>
            {loading ? <LoadingBlock /> : tree.length === 0 ? (
                <EmptyState icon={Layers} title="No categories yet" description="Create your first category to organize products."
                    action={<Button onClick={() => setDialogOpen(true)}><Plus className="h-4 w-4 mr-2" />Add Category</Button>} />
            ) : (
                <Card className="p-2" data-testid="categories-tree-card">
                    <TreeView tree={tree} onEdit={(x) => { setEditing(x); setDialogOpen(true); }} onDelete={doDelete} />
                </Card>
            )}
            <CategoryDialog open={dialogOpen} onOpenChange={setDialogOpen} editing={editing} parents={flat} onSaved={load} />
        </AppShell>
    );
}
